/**
 * "What I read" — the code-written tail of a Lane V2 answer, split off the answer body (leg `gui-what-i-read`).
 *
 * MSA appends one machine-written block as the LAST thing of an answer (`shared/evidence_section.py`):
 *
 *     ...answer...
 *
 *     ## Data results      (optional; folded into the same footer disclosure as the queries it lists, `Queries (M)`)
 *
 *     ## Evidence
 *
 *     Sources read: 7
 *
 *     - [Source code: AccountTriggerHandler](artifact://<id>)
 *     - Metadata search for "contract renewal"
 *
 * An answer that read nothing carries `Sources read: 0. This answer was written without reading the org.`
 *
 * Recognising the heading is a structural parse of the product's OWN output (the same split MSA's `split_tail` does), never a
 * reading of what a human or the model meant. Stored answers from before the `Sources read:` line have only the list lines.
 */

const HEADING = "## Evidence";
const DATA_HEADING = "## Data results";
/** The lines `data_block` writes: `- Query N: ...`, `  \`soql\``, `- and K more queries`. Anything else above Evidence is the answer's own. */
const DATA_LINE = /^(- Query \d{1,3}: .*|  `.*`|- and (\d{1,6}) more queries)$/;
const SOURCES_PREFIX = "Sources read:";
/** `- and K more` (the cap line of the list). */
const MORE_LINE = /^- and (\d{1,6}) more$/;
/** The three line shapes of a tail stored before the `Sources read:` line: `- \`id\``, `- [name](artifact://id)`, `- and K more`. */
const OLD_TAIL_LINE = /^(- `[^`\n]+`|- \[[^\]\n]+\]\(artifact:\/\/[^)\s]+\)|- and \d{1,6} more)$/;

export interface SourcesTail {
  /** The answer without its code-written tail (`## Data results` and `## Evidence` both leave the body). */
  body: string;
  /** The `## Data results` lines (queries and their raw SOQL), as markdown without the heading; "" when the tail carried none. */
  queriesMarkdown: string;
  /** How many queries the section lists (its `- Query N` lines plus the `- and K more queries` cap line); 0 when none. */
  queryCount: number;
  /** The list lines of the tail (`- ...`), as markdown, one per read; "" when the tail lists none. */
  listMarkdown: string;
  /** The count the tail states (`Sources read: N`), else the number of list lines. */
  readCount: number;
  /** True when the tail carried its own `Sources read: N` line. */
  stated: boolean;
}

/** The count in a `Sources read: N` line (also the zero line's `Sources read: 0. ...`), or null. */
function statedCount(line: string): number | null {
  const bare = line.trim();
  if (!bare.startsWith(SOURCES_PREFIX)) return null;
  const m = /^\s*(\d{1,6})\b/.exec(bare.slice(SOURCES_PREFIX.length));
  return m ? Number(m[1]) : null;
}

/** `markdown` split at the LAST `\n\n## Evidence\n`; null when the answer has no such tail. */
export function splitSourcesTail(markdown: string | null | undefined): SourcesTail | null {
  if (!markdown) return null;
  const at = markdown.lastIndexOf(`\n\n${HEADING}\n`);
  if (at < 0) return null;
  const tailLines = markdown.slice(at + 2 + HEADING.length).split("\n");
  let stated: number | null = null;
  const list: string[] = [];
  let listed = 0;
  let allOldShapes = true;
  for (const line of tailLines) {
    if (!line.trim()) continue;
    const n = statedCount(line);
    if (n !== null) {
      if (stated === null) stated = n;
      continue;
    }
    const more = MORE_LINE.exec(line.trim());
    if (more) listed += Number(more[1]);
    else if (line.trimStart().startsWith("- ")) listed += 1;
    if (!OLD_TAIL_LINE.test(line.trim())) allOldShapes = false;
    if (line.trimStart().startsWith("- ")) list.push(line.trimEnd());
  }
  // Only a tail the code wrote collapses: it states `Sources read:` (always, zero included), or (stored before that line existed)
  // every line is one of the shapes `evidence_section.render` writes. A model-written `## Evidence` stays ordinary markdown.
  if (stated === null && !allOldShapes) return null;
  const data = splitDataResults(markdown, at);
  return {
    body: markdown.slice(0, data?.start ?? at),
    queriesMarkdown: data?.markdown ?? "",
    queryCount: data?.count ?? 0,
    listMarkdown: list.join("\n"),
    readCount: stated ?? listed,
    stated: stated !== null,
  };
}

/**
 * The code's `## Data results` section sitting DIRECTLY above the Evidence tail at `evidenceAt`: found only when every line between
 * its heading and the tail is one `data_block` writes (the same guard MSA's `split_tail` applies), so a model-written heading of the
 * same name stays ordinary answer text.
 */
function splitDataResults(markdown: string, evidenceAt: number): { start: number; markdown: string; count: number } | null {
  const at = markdown.lastIndexOf(`\n\n${DATA_HEADING}\n`, evidenceAt);
  if (at < 0) return null;
  const lines = markdown.slice(at + 2 + DATA_HEADING.length, evidenceAt).split("\n").filter((l) => l.trim());
  if (lines.length === 0 || !lines.every((l) => DATA_LINE.test(l.trimEnd()))) return null;
  let count = 0;
  for (const l of lines) {
    const more = /^- and (\d{1,6}) more queries$/.exec(l.trimEnd());
    if (more) count += Number(more[1]);
    else if (l.startsWith("- Query ")) count += 1;
  }
  return { start: at, markdown: lines.map((l) => l.trimEnd()).join("\n"), count };
}

/**
 * True when the answer's tail says it read nothing from the org: the tail states `Sources read: 0` (or, stored before that line, lists
 * nothing and the turn made no calls) and no query is listed. The completion row shows this as a muted tag instead of its own line.
 */
export function readNothing(tail: SourcesTail | null, toolCalls: number | null | undefined): boolean {
  return !!tail && tail.queryCount === 0 && sourcesReadCount(tail, toolCalls) === 0;
}

/**
 * The N the summary shows: what the tail states (`Sources read: N`, one owner: the same call log that wrote the list under it).
 * A tail stored before that line falls back to the turn's R5 `trace.tool_calls`, then to the list-line count.
 */
export function sourcesReadCount(tail: SourcesTail, toolCalls: number | null | undefined): number {
  if (tail.stated) return tail.readCount;
  return typeof toolCalls === "number" && toolCalls >= 0 ? toolCalls : tail.readCount;
}

/**
 * MSA leg `notion-e2e`: one listed read about a CONNECTED external tool (Notion, Jira, GitHub): the backend's own noun
 * ("Connected tool search for …", "Connected tool record"), linked or plain, or the raw `connectors.*` tool name an answer
 * stored before that noun carries. An "Attachment" line (the saved rest of a long record) and the "and K more" cap are neutral.
 */
const CONNECTED_READ_LINE = /^- \[?(Connected tool (search|record)\b|connectors\.[a-z_]+)/;
const NEUTRAL_READ_LINE = /^(- \[?Attachment\b|- and \d{1,6} more$)/;

/**
 * True when every read the tail lists is a connected-tool read (at least one) and no org query is listed: the answer is about a
 * page, issue or record in a connected tool, so Salesforce-shaped follow-ups ("Show callers", "Show tests") would not fit it.
 */
export function readsOnlyConnectedTools(tail: SourcesTail | null): boolean {
  if (!tail || tail.queryCount > 0) return false;
  const lines = tail.listMarkdown.split("\n").map((l) => l.trim()).filter((l) => l.startsWith("- "));
  const reads = lines.filter((l) => !NEUTRAL_READ_LINE.test(l));
  return reads.length > 0 && reads.every((l) => CONNECTED_READ_LINE.test(l));
}
